import axios, { AxiosRequestConfig, AxiosResponse } from "axios"
import { useApi } from "../components/AppContext"
import {
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query"


/**
 * Options for API fetch requests
 */
interface ApiFetchOptions extends Omit<AxiosRequestConfig, "url"> {
  body?: unknown // For backward compatibility while retaining type-safety
  params?: Record<string, unknown>
}

/**
 * Perform an HTTP request via axios to API base URL + path, throwing on errors.
 * @param {string} apiUrl  Base API URL from context
 * @param {string} path    Path including leading slash (e.g., '/projects')
 * @param {ApiFetchOptions} options  Axios request options (method, headers, data, params)
 * @returns {Promise<any>}  Parsed JSON response data
 */
export async function apiFetch<T = unknown>(
  apiUrl: string,
  path: string,
  options: ApiFetchOptions = {}
): Promise<T> {
  const url = `${apiUrl}${path}`
  try {
    // Always send auth cookies; allow caller override.
    const {
      method = "get",
      headers,
      body,
      data,
      params,
      withCredentials = true,
      ...axiosRest
    } = options
    const response: AxiosResponse = await axios({
      url,
      method,
      headers,
      data: body ?? data,
      params,
      withCredentials,
      ...axiosRest,
    })
    return response.data as T
  } catch (err: unknown) {
    if (axios.isAxiosError(err) && err.response) {
      const msg =
        typeof err.response.data === "string"
          ? err.response.data
          : JSON.stringify(err.response.data)
      throw new Error(
        `API request failed (${err.response.status}): ${err.response.statusText} ${msg}`
      )
    }
    throw err
  }
}

/**
 * Options for the useApiQuery hook
 */
export interface UseApiQueryOptions<TData = unknown, TError = unknown>
  extends Omit<UseQueryOptions<TData, TError, TData>, "queryKey" | "queryFn"> {
  staleTime?: number
  enabled?: boolean
  fetchOptions?: ApiFetchOptions
  path?: string // Optional override for the path derived from queryKey
}

/**
 * React hook wrapping react-query to call the API.
 * @param {string | any[]} path  API path including leading slash or query key array
 * @param {UseApiQueryOptions} options  Options: { staleTime, enabled, fetchOptions, ...rest }
 * @returns {UseQueryResult}  react-query result
 */
export function useApiQuery<TData = unknown, TError = unknown>(
  path: string | unknown[],
  options: UseApiQueryOptions<TData, TError> = {}
): UseQueryResult<TData, TError> {
  const apiUrl = useApi()
  const {
    staleTime = 600000,
    enabled = true,
    fetchOptions,
    path: overridePath,
    ...rest
  } = options

  // Normalise queryKey to array and derive API path if not overridden
  const keyArray: readonly unknown[] = Array.isArray(path) ? path : [path]
  const apiPath =
    overridePath ??
    (keyArray.every((seg) => typeof seg === "string" || typeof seg === "number")
      ? "/" + keyArray.join("/")
      : String(keyArray[0]))

  const queryFn = async (): Promise<TData> => {
    return apiFetch<TData>(apiUrl, apiPath, fetchOptions)
  }

  // Simplified 3-argument form avoids the complex generic tuple that
  // caused type-checking issues with recent react-query versions.
  const queryResult = useQuery<TData, TError>(keyArray, queryFn, {
    staleTime,
    enabled,
    ...rest,
  })

  return queryResult
}
